const { useState, useEffect, useCallback, useRef, RawHTML } = wp.element;
const {
    BlockEditorProvider, 
    BlockList,
    DefaultBlockAppender,
    WritingFlow, 
    ObserveTyping,
    BlockInspector,
    BlockTools,
    mediaUpload
} = wp.blockEditor;
const {
    SlotFillProvider, 
    Popover,
    Button,
    Panel,
    PanelBody,
    TextControl,
    TextareaControl
} =  wp.components;
import { InterfaceSkeleton } from "@wordpress/interface";
const { parse, serialize } = wp.blocks;
const { __ } = wp.i18n;
const apiFetch = wp.apiFetch;
import GiveawayPreview from './components/GiveawayPreview';

// Editor settings with media upload support
const editorSettings = {
    mediaUpload: ( { onFileChange, allowedTypes, filesList } ) => {
        mediaUpload( {
            filesList,
            allowedTypes,
            onFileChange,
        } );
    },
};

const App = () => {
    const [ blocks, updateBlocks ] = useState( [] );
    const [ actionBlocks, updateActionBlocks ] = useState( [] );
    const [ isSaving, setIsSaving ] = useState( false );
    const [ activeTab, setActiveTab ] = useState( 'builder' ); // 'builder' or 'preview'
    const [ prizeData, setPrizeData ] = useState( {
        title: '',
        description: '',
        imageUrl: ''
    } );
    const [ collapsedSections, setCollapsedSections ] = useState( {
        description: false,
        prizes: false,
        actions: false,
        metaboxes: false
    } );
    const metaboxesRef = useRef( null );

    const toggleSection = useCallback( ( section ) => {
        setCollapsedSections( prev => ( {
            ...prev,
            [ section ]: !prev[ section ]
        } ) );
    }, [] );

    useEffect( () => {
        const initialBlocks = parse( window.giveasapBuilderData.content || '' );
        updateBlocks( initialBlocks );
        
        // Load prize data if available
        if ( window.giveasapBuilderData.prizeData ) {
            setPrizeData( window.giveasapBuilderData.prizeData );
        }
        
        // Load action blocks if available
        if ( window.giveasapBuilderData.actionContent ) {
            const initialActionBlocks = parse( window.giveasapBuilderData.actionContent );
            updateActionBlocks( initialActionBlocks );
        }

        // Move metaboxes from hidden container to our ref
        const metaboxesContainer = document.getElementById( 'giveasap-metaboxes-container' );
        if ( metaboxesContainer && metaboxesRef.current ) {
            while ( metaboxesContainer.firstChild ) {
                metaboxesRef.current.appendChild( metaboxesContainer.firstChild );
            }
            metaboxesContainer.remove();
        }
    }, [] );

    const handleSave = () => {
        setIsSaving( true );
        const content = serialize( blocks );
        const actionContent = serialize( actionBlocks );

        apiFetch( {
            path: `/wp/v2/giveasap/${window.giveasapBuilderData.giveawayID}`,
            method: 'POST',
            data: {
                content: content,
                meta: {
                    _giveasap_prize_data: JSON.stringify( prizeData ),
                    _giveasap_action_content: actionContent
                }
            },
        } ).then( () => {
            setIsSaving( false );
            alert( __( 'Giveaway saved successfully!', 'giveasap' ) );
        } ).catch( ( error ) => {
            setIsSaving( false );
            console.error( 'Error saving giveaway:', error );
            alert( __( 'Error saving giveaway.', 'giveasap' ) );
        } );
    };

    return (
      <SlotFillProvider>
        <div className="giveasap-builder-wrapper">
          {/* Header */}
          <div className="giveasap-builder-header">
            <div className="giveasap-builder-header__title">
              <h1>{window.giveasapBuilderData.title}</h1>
            </div>
            <div className="giveasap-builder-header__actions">
              <div className="giveasap-builder-tabs">
                <Button
                  isPressed={activeTab === 'builder'}
                  onClick={() => setActiveTab('builder')}
                  className="giveasap-tab-button"
                >
                  {__("Builder", "giveasap")}
                </Button>
                <Button
                  isPressed={activeTab === 'preview'}
                  onClick={() => setActiveTab('preview')}
                  className="giveasap-tab-button"
                >
                  {__("Preview", "giveasap")}
                </Button>
              </div>
              {activeTab === 'builder' && (
                <Button
                  isPrimary
                  onClick={handleSave}
                  isBusy={isSaving}
                  disabled={isSaving}
                >
                  {__("Save Giveaway", "giveasap")}
                </Button>
              )}
            </div>
          </div>

          <div className="giveasap-builder-main">
            {activeTab === 'builder' ? (
              <>
              {/* Main Content Area */}
              <div className="giveasap-builder-sections">
                
                {/* Section 1: Description (post_content) */}
                <div className={`giveasap-builder-section giveasap-section-description ${collapsedSections.description ? 'is-collapsed' : ''}`}>
                  <div className="giveasap-section-header" onClick={() => toggleSection('description')}>
                    <div className="giveasap-section-header-content">
                      <h2>{__("Description", "giveasap")}</h2>
                      <p className="giveasap-section-description">{__("Edit the giveaway description content.", "giveasap")}</p>
                    </div>
                    <Button
                      icon={collapsedSections.description ? 'arrow-down' : 'arrow-up'}
                      label={collapsedSections.description ? __("Expand", "giveasap") : __("Collapse", "giveasap")}
                      onClick={(e) => { e.stopPropagation(); toggleSection('description'); }}
                    />
                  </div>
                  {!collapsedSections.description && (
                    <BlockEditorProvider
                      value={blocks}
                      onInput={(newBlocks) => updateBlocks(newBlocks)}
                      onChange={(newBlocks) => updateBlocks(newBlocks)}
                      settings={editorSettings}
                    >
                      <div className="giveasap-section-with-sidebar">
                        <div className="giveasap-section-content">
                          <BlockTools>
                            <WritingFlow>
                              <ObserveTyping>
                                <BlockList renderAppender={DefaultBlockAppender} />
                              </ObserveTyping>
                            </WritingFlow>
                          </BlockTools>
                        </div>
                        <div className="giveasap-section-sidebar">
                          <BlockInspector />
                        </div>
                      </div>
                      <Popover.Slot />
                    </BlockEditorProvider>
                  )}
                </div>

                {/* Section 2: Prizes */}
                <div className={`giveasap-builder-section giveasap-section-prizes ${collapsedSections.prizes ? 'is-collapsed' : ''}`}>
                  <div className="giveasap-section-header" onClick={() => toggleSection('prizes')}>
                    <div className="giveasap-section-header-content">
                      <h2>{__("Prizes", "giveasap")}</h2>
                      <p className="giveasap-section-description">{__("Configure the prize details for this giveaway.", "giveasap")}</p>
                    </div>
                    <Button
                      icon={collapsedSections.prizes ? 'arrow-down' : 'arrow-up'}
                      label={collapsedSections.prizes ? __("Expand", "giveasap") : __("Collapse", "giveasap")}
                      onClick={(e) => { e.stopPropagation(); toggleSection('prizes'); }}
                    />
                  </div>
                  {!collapsedSections.prizes && (
                  <div className="giveasap-section-content giveasap-prizes-content">
                    <Panel>
                      <PanelBody title={__("Prize Settings", "giveasap")} initialOpen={true}>
                        <TextControl
                          label={__("Prize Title", "giveasap")}
                          value={prizeData.title}
                          onChange={(value) => setPrizeData({...prizeData, title: value})}
                          placeholder={__("Enter prize title...", "giveasap")}
                        />
                        <TextareaControl
                          label={__("Prize Description", "giveasap")}
                          value={prizeData.description}
                          onChange={(value) => setPrizeData({...prizeData, description: value})}
                          placeholder={__("Enter prize description...", "giveasap")}
                        />
                        <TextControl
                          label={__("Prize Image URL", "giveasap")}
                          value={prizeData.imageUrl}
                          onChange={(value) => setPrizeData({...prizeData, imageUrl: value})}
                          placeholder={__("Enter image URL...", "giveasap")}
                        />
                        {prizeData.imageUrl && (
                          <div className="giveasap-prize-preview">
                            <img src={prizeData.imageUrl} alt={prizeData.title} style={{maxWidth: '100%', marginTop: '10px'}} />
                          </div>
                        )}
                      </PanelBody>
                    </Panel>
                  </div>
                  )}
                </div>

                {/* Section 3: Actions */}
                <div className={`giveasap-builder-section giveasap-section-actions ${collapsedSections.actions ? 'is-collapsed' : ''}`}>
                  <div className="giveasap-section-header" onClick={() => toggleSection('actions')}>
                    <div className="giveasap-section-header-content">
                      <h2>{__("Entry Actions", "giveasap")}</h2>
                      <p className="giveasap-section-description">{__("Configure actions users can complete to earn entries.", "giveasap")}</p>
                    </div>
                    <Button
                      icon={collapsedSections.actions ? 'arrow-down' : 'arrow-up'}
                      label={collapsedSections.actions ? __("Expand", "giveasap") : __("Collapse", "giveasap")}
                      onClick={(e) => { e.stopPropagation(); toggleSection('actions'); }}
                    />
                  </div>
                  {!collapsedSections.actions && (
                    <BlockEditorProvider
                      value={actionBlocks}
                      onInput={(newBlocks) => updateActionBlocks(newBlocks)}
                      onChange={(newBlocks) => updateActionBlocks(newBlocks)}
                      settings={editorSettings}
                    >
                      <div className="giveasap-section-with-sidebar">
                        <div className="giveasap-section-content">
                          <BlockTools>
                            <WritingFlow>
                              <ObserveTyping>
                                <BlockList renderAppender={DefaultBlockAppender} />
                              </ObserveTyping>
                            </WritingFlow>
                          </BlockTools>
                        </div>
                        <div className="giveasap-section-sidebar">
                          <BlockInspector />
                        </div>
                      </div>
                      <Popover.Slot />
                    </BlockEditorProvider>
                  )}
                </div>

                {/* Section 4: Metaboxes */}
                <div className={`giveasap-builder-section giveasap-section-metaboxes ${collapsedSections.metaboxes ? 'is-collapsed' : ''}`}>
                  <div className="giveasap-section-header" onClick={() => toggleSection('metaboxes')}>
                    <div className="giveasap-section-header-content">
                      <h2>{__("Giveaway Settings", "giveasap")}</h2>
                      <p className="giveasap-section-description">{__("Configure additional giveaway settings.", "giveasap")}</p>
                    </div>
                    <Button
                      icon={collapsedSections.metaboxes ? 'arrow-down' : 'arrow-up'}
                      label={collapsedSections.metaboxes ? __("Expand", "giveasap") : __("Collapse", "giveasap")}
                      onClick={(e) => { e.stopPropagation(); toggleSection('metaboxes'); }}
                    />
                  </div>
                  {!collapsedSections.metaboxes && (
                    <div className="giveasap-section-content giveasap-metaboxes-content" ref={metaboxesRef}>
                    </div>
                  )}
                </div>

              </div>
              </>
            ) : (
              <GiveawayPreview giveawayId={window.giveasapBuilderData.giveawayID} />
            )}
          </div>
        </div>
      </SlotFillProvider>
    );
};

export default App;
